import React, { createContext, useReducer, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import { saveProfile } from '@spa/shared/utils/user';
import { useStorage } from '@spa/shared/hooks/storage';
import { useDebounce } from '@spa/shared/hooks/debounce';
import { ModalContext } from '@spa/shared/contexts/modal';
import { ProfileContext } from '@spa/shared/contexts/profile';
import { replaceGuestWishlist } from '@spa/shared/clients/core';
import { modalReducerActions } from '@spa/shared/reducers/modal';
import { WishlistReducer, WishlistReducerActions } from './reducer';
import { modalIds } from '@spa/shared/components/Modal/modalDisplayer';
import { toApplicationSchema, toCoreSchema } from '@spa/shared/utils/wishlistTransformer';

const API_REQUEST_DELAY_IN_MILISECONDS = 500;
const WishlistContext = createContext();

const WishlistContextProvider = ({ children }) => {
  const storage = useStorage('sessionStorage');
  const { profile } = useContext(ProfileContext);
  const wishlistInput = profile && profile.wish_list;
  const userId = profile && profile.id;
  const [{ wishlist, isToMakeApiRequest }, dispatch] = useReducer(WishlistReducer, {
    wishlist: {},
    isToMakeApiRequest: false
  });
  const modalContext = useContext(ModalContext);
  const { query: { city } = {} } = useRouter();
  const debouncedState = useDebounce(wishlist, API_REQUEST_DELAY_IN_MILISECONDS);

  useEffect(() => {
    if (!wishlistInput) {
      return;
    }

    dispatch({ type: WishlistReducerActions.LOAD_WISHLIST, payload: toApplicationSchema(wishlistInput) });
  }, [wishlistInput]);

  useEffect(() => {
    if (!isToMakeApiRequest) {
      return;
    }

    replaceGuestWishlist(userId, toCoreSchema(wishlist))
      .then(() => saveProfile(storage, profile))
      .catch(() => console.error('could not update the wishlist'));
  }, [debouncedState]);

  const addToWishlist = useCallback(
    offerId => {
      if (!userId) {
        modalContext.dispatch({ type: modalReducerActions.SHOW, payload: { modalId: modalIds.WISHLIST } });

        return false;
      }

      dispatch({ type: WishlistReducerActions.ADD_OFFER, payload: { offerId, city } });

      return true;
    },
    [city, userId]
  );

  const removeFromWishlist = useCallback(
    offerId => {
      dispatch({ type: WishlistReducerActions.REMOVE_OFFER, payload: { offerId, city } });

      return true;
    },
    [city]
  );

  const isOnWishlist = useCallback(
    offerId => {
      const offersInCity = wishlist[city] || [];

      return offersInCity.includes(parseInt(offerId, 10));
    },
    [wishlist, city]
  );

  return (
    <WishlistContext.Provider value={{ addToWishlist, removeFromWishlist, isOnWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
};

WishlistContextProvider.propTypes = {
  wishlist: PropTypes.object,
  userId: PropTypes.string,
  children: PropTypes.node
};

export { WishlistContext, WishlistContextProvider };
